import type { MattersToYouGroup } from "common/apis";
import { useAccessibility, useI18N } from "common/hooks";
import { resolveMattersToYouGroup } from "common/utils/mattersToYouGroup";

import OnboardingModal from "../components/OnboardingModal";
import OnboardingOptionRow from "../components/OnboardingOptionRow";
import OnboardingPrimaryButton from "../components/OnboardingPrimaryButton";
import OnboardingStepHeader from "../components/OnboardingStepHeader";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useOnboardingStepNavigation } from "../state/useOnboardingStepNavigation";
import { resolveAccessibilityIcon } from "../utils/accessibilityIcons";

/**
 * Step 3 of 4 — "What should we look for first?".
 *
 * Rows come from `/accessibility/load` → `mattersToYou`, through the shared
 * `useAccessibility()` hook the rest of the app already uses (query key
 * `["accessibilities"]`) rather than a second request for the same endpoint.
 *
 * Selecting a group means the user cares about every tag in its `children`; we
 * persist the **`group_name`** and the group's permanent `id`, not the child
 * ids, so a stored answer survives the backend re-grouping the tags or an admin
 * renaming the group. Resolve the group from the live response at the point we
 * use it - @see `resolveMattersToYouGroup`.
 *
 * **One answer, not several.** The frames draw radios, and the question is
 * which single feature to set the homepage up around — so picking a row
 * replaces whatever was picked before. `accessNeeds` stays an array (it is what
 * every reader of the answer expects, and "none" is still the empty one), it
 * just never holds more than one entry from here.
 *
 * **There is no "no specific access needs" opt-out** — the frames draw a footer
 * with Continue and nothing else, so the only way on is to pick a row. The one
 * exception is a step with no rows to pick: see `canContinue`.
 */
const answerFor = (group: MattersToYouGroup) => ({
  accessNeeds: [group.group_name],
  accessNeedGroupId: typeof group.id === "number" ? group.id : null,
});

const AccessNeedsStep = () => {
  const i18n = useI18N();
  const { answers, updateAnswers, dismiss } = useOnboardingFlow();
  const { stepNumber, totalSteps, goNext } = useOnboardingStepNavigation();
  const { mattersToYou, isAccessibilityLoading } = useAccessibility();

  const { accessNeeds, accessNeedGroupId } = answers;
  const title = i18n.get("onboardingAccessNeedsTitle");
  const selectedGroup = resolveMattersToYouGroup(mattersToYou, {
    id: accessNeedGroupId,
    name: accessNeeds[0],
  });

  // Radio, so a second click on the picked row leaves it picked rather than
  // emptying the answer behind the user's back. With no opt-out in the footer,
  // clearing the answer would strand them on a disabled Continue.
  const selectGroup = (group: MattersToYouGroup) => {
    updateAnswers(answerFor(group));
  };

  // A pick made before a rename is saved under the group's current name.
  const continueWithSelection = () => {
    updateAnswers({ accessNeedsAnswered: true, ...(selectedGroup && answerFor(selectedGroup)) });
    goNext();
  };

  /**
   * Continue waits for a pick, as the frames draw it — *unless* there is
   * nothing to pick.
   *
   * The rows are remote, so a failed or empty `/accessibility/load` would
   * otherwise be a dead end: no rows, no opt-out in the footer, and a Continue
   * that never enables.
   */
  const canContinue =
    !!selectedGroup || (!isAccessibilityLoading && !mattersToYou.length);

  return (
    <OnboardingModal
      onClose={dismiss}
      ariaLabel={title}
      footer={
        <OnboardingPrimaryButton
          label={i18n.get("continue")}
          disabled={!canContinue}
          onClick={continueWithSelection}
        />
      }
    >
      <div className="flex flex-col gap-4">
        <OnboardingStepHeader
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          title={title}
          subtitle={i18n.get("onboardingAccessNeedsSubtitle")}
        />

        {/* Figma's `profile-rows`: 8pt between rows, one group so a screen
            reader announces "1 of 5" rather than five loose radios. */}
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="sr-only">{title}</legend>

          {mattersToYou.map((group) => (
            <OnboardingOptionRow
              key={group.group_name}
              name="onboarding-access-needs"
              label={group.group_name}
              icon={resolveAccessibilityIcon(group.icon)}
              selected={selectedGroup === group}
              onSelect={() => selectGroup(group)}
            />
          ))}
        </fieldset>

        <p aria-live="polite" className="sr-only">
          {isAccessibilityLoading ? i18n.get("loading") : ""}
        </p>

        {!isAccessibilityLoading && mattersToYou.length === 0 ? (
          <p className="body-m-medium text-center text-fg-neutral-subtle">
            {i18n.get("onboardingAccessNeedsUnavailable")}
          </p>
        ) : null}
      </div>
    </OnboardingModal>
  );
};

export default AccessNeedsStep;
